import React, {useState} from 'react';
import {Button, DatePicker, Input, Space, Tooltip, message} from 'antd';
import {SearchOutlined} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Highlighter from 'react-highlight-words';
import {EyeIcon} from "@heroicons/react/24/outline";
import {getPatientReport} from "../../services/reports/reportsService";
import * as XLSX from "xlsx";
import {RiFileExcel2Fill} from "react-icons/ri";
import {
    ActionButton,
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
    RowIconButton,
} from "../../components/ui";

const {RangePicker} = DatePicker;

const PatientsReport = () => {
    const { t } = useTranslation();
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [dateRange, setDateRange] = useState([null, null]);

    const handleShowReport = async () => {
        setLoading(true);
        try {
            const start_at = dateRange[0] ? dateRange[0]?.format("YYYY-MM-DD") : '';
            const end_at = dateRange[1] ? dateRange[1]?.format("YYYY-MM-DD") : '';
            const apiResponse = await getPatientReport(start_at, end_at);
            if (apiResponse && apiResponse.data) {
                // Добавляем ключ "key" к данным для таблицы для уникальности строк
                const dataWithKey = apiResponse.data.map((item, index) => ({...item, key: index + 1}));
                setReportData(dataWithKey);
            } else {
                setReportData([]);
            }
        } catch (error) {
            console.error('Ошибка при получении данных о пациентах:', error);
        } finally {
            setLoading(false);
        }
    };


    const handleRangePickerChange = (dates) => {
        setDateRange(dates);
        // При изменении диапазона дат в RangePicker, даты сохраняются в state
    };


    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters}) => (
            <div style={{padding: 8}}>
                <Input
                    placeholder={t('reports.searchBy', { field: dataIndex })}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{marginBottom: 8, display: 'block'}}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined/>}
                        size="small"
                        style={{width: 90}}
                    >
                        {t('reports.search')}
                    </Button>
                    <Button
                        onClick={() => handleReset(clearFilters)}
                        size="small"
                        style={{width: 90}}
                    >
                        {t('reports.reset')}
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{color: filtered ? '#1890ff' : undefined}}/>
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : '',
    });

    const renderHighlighted = (text, dataIndex) =>
        searchedColumn === dataIndex ? (
            <Highlighter
                highlightStyle={{backgroundColor: '#1D2C7B', padding: 0, color: '#fff'}}
                searchWords={[searchText]}
                autoEscape
                textToHighlight={text ? text.toString() : ''}
            />
        ) : (
            text
        );

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const columns = [
        {
            title: t('patients.colId'),
            dataIndex: 'key',
            key: 'key',
            width: 72,
            sorter: (a, b) => a.key - b.key,
            ...getColumnSearchProps('key'),
            render: (text) => (
                <span className="text-xs text-slate-500 tabular-nums">{renderHighlighted(text, 'key')}</span>
            ),
        },
        {
            title: t('common.name'),
            dataIndex: 'name',
            key: 'name',
            ellipsis: true,
            sorter: (a, b) => a.name.localeCompare(b.name),
            ...getColumnSearchProps('name'),
            render: (text) => (
                <span className="text-sm font-medium text-[#1D2C7B]">{renderHighlighted(text, 'name')}</span>
            ),
        },
        {
            title: t('patients.birthDate'),
            dataIndex: 'birth_at',
            key: 'birth_at',
            width: 150,
            sorter: (a, b) => new Date(a.birth_at) - new Date(b.birth_at),
            ...getColumnSearchProps('birth_at'),
            render: (text) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">
                    {renderHighlighted(text, 'birth_at')}
                </span>
            ),
        },
        {
            title: t('common.phone'),
            dataIndex: 'phone',
            key: 'phone',
            width: 170,
            sorter: (a, b) => a.phone.localeCompare(b.phone),
            ...getColumnSearchProps('phone'),
            render: (text) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">
                    {renderHighlighted(text, 'phone')}
                </span>
            ),
        },
        {
            title: t('common.actions'),
            key: 'actions',
            width: 110,
            render: () => (
                <Tooltip title={t('reports.info')}>
                    <RowIconButton>
                        <EyeIcon className="w-[18px] h-[18px]"/>
                    </RowIconButton>
                </Tooltip>
            ),
        },
    ];

    const renderMobileCard = (record) => (
        <div>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-[#1D2C7B]">{record.name}</span>
                    <span className="mt-0.5 block text-xs text-slate-500 tabular-nums">№ {record.key}</span>
                </div>
                <Tooltip title={t('reports.info')}>
                    <RowIconButton className="shrink-0">
                        <EyeIcon className="w-[18px] h-[18px]"/>
                    </RowIconButton>
                </Tooltip>
            </div>

            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
                <div className="min-w-0">
                    <span className="block text-[11px] leading-tight text-slate-500">{t('patients.birthDate')}</span>
                    <span className="block text-sm text-slate-700 tabular-nums">{record.birth_at}</span>
                </div>
                <div className="min-w-0">
                    <span className="block text-[11px] leading-tight text-slate-500">{t('common.phone')}</span>
                    <span className="block text-sm text-slate-700 tabular-nums">{record.phone}</span>
                </div>
            </div>
        </div>
    );

    const exportToExcel = () => {
        if (reportData.length === 0) {
            message.warning(t('reports.noExport'));
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(reportData.map(({key, ...rest}) => rest)); // Убираем поле key
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

        // Генерация файла Excel
        XLSX.writeFile(workbook, 'BemorReport.xlsx');
    };


    const handleEdit = (key) => {
        console.log(`Редактировать запись с ключом: ${key}`);
    };

    const handleDelete = (key) => {
        console.log(`Удалить запись с ключом: ${key}`);
    };

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('reports.patients')}
                    count={reportData.length}
                    filters={
                        <RangePicker
                            size="small"
                            className="w-full md:!w-[250px]"
                            onChange={handleRangePickerChange}
                            value={dateRange} // Значение должно быть dateRange, а не startDate и endDate
                        />
                    }
                    actions={
                        <>
                            <ActionButton
                                collapseOnMobile={false}
                                onClick={handleShowReport}
                                loading={loading}
                            >
                                {t('reports.show')}
                            </ActionButton>
                            <Button
                                type="default"
                                className="!h-9 !rounded-lg !inline-flex !items-center !gap-1.5"
                                icon={<RiFileExcel2Fill/>}
                                onClick={exportToExcel}
                                disabled={reportData.length === 0}
                            >
                                <span className="hidden sm:inline">{t('reports.exportExcel')}</span>
                            </Button>
                        </>
                    }
                />

                <DataTable
                    dataSource={reportData}
                    columns={columns}
                    mobileCard={renderMobileCard}
                    scrollX={860}
                    loading={loading}
                    rowKey="key"
                    pagination={{
                        showSizeChanger: false,
                        position: ['bottomCenter'],
                        className: 'px-4 pb-3',
                    }}
                />
            </PageCard>
        </PageShell>
    );
};

export default PatientsReport;
